import { isLightColor, parseHexColor } from './color'

/**
 * Builds a film-grain background image as a CSS `url("data:…")` value.
 *
 * The grain is continuous-tone fractal noise, desaturated to grey, then pushed
 * toward black by a gamma curve so it ends up "mostly black with bright specks".
 * Pair it with `background-blend-mode: screen`: the black areas leave the base
 * colour untouched and only the bright peaks lift — fine grain without washing
 * the colour out.
 *
 * Tip: the grain cell size is `1 / baseFrequency` CSS px. Keep `baseFrequency`
 * at ~0.5 or below so cells are >= 2px and stay visible on 1x (non-retina)
 * screens; finer grain only resolves on retina displays.
 */
export type GrainOptions = {
  /** Noise frequency — higher = finer grain, lower = chunkier. */
  baseFrequency?: number
  /** Layers of noise detail. More octaves = richer but busier. */
  numOctaves?: number
  /** Gamma applied to the grey noise. Higher = darker/sparser specks. */
  exponent?: number
  /** Caps the brightness of the specks (1 = full). */
  amplitude?: number
  /** Tile size in px (also the SVG canvas); larger = less visible repetition. */
  tileSize?: number
  /** Dark specks on white instead, for `background-blend-mode: multiply`. */
  invert?: boolean
}

export function buildGrainBackground({
  baseFrequency = 0.8,
  numOctaves = 2,
  exponent = 9,
  amplitude = 1,
  tileSize = 100,
  invert = false,
}: GrainOptions = {}): string {
  const gamma = `type="gamma" exponent="${exponent}" amplitude="${amplitude}" offset="0"`
  // Inverted in sRGB, after the linear-RGB grain, so dark specks mirror the
  // bright ones instead of being squashed toward white.
  const inversion = invert
    ? `<feComponentTransfer color-interpolation-filters="sRGB">` +
      `<feFuncR type="table" tableValues="1 0"/><feFuncG type="table" tableValues="1 0"/><feFuncB type="table" tableValues="1 0"/>` +
      `</feComponentTransfer>`
    : ''
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${tileSize}" height="${tileSize}">` +
    `<filter id="g">` +
    `<feTurbulence type="fractalNoise" baseFrequency="${baseFrequency}" numOctaves="${numOctaves}" stitchTiles="stitch"/>` +
    `<feColorMatrix type="saturate" values="0"/>` +
    `<feComponentTransfer>` +
    `<feFuncR ${gamma}/><feFuncG ${gamma}/><feFuncB ${gamma}/>` +
    `<feFuncA type="discrete" tableValues="1"/>` +
    `</feComponentTransfer>` +
    inversion +
    `</filter>` +
    `<rect width="100%" height="100%" filter="url(#g)"/>` +
    `</svg>`

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

const BRAND_GRAIN_TILE_SIZE = 300
// How far the brightest speck moves a channel, whatever the colour.
const BRAND_GRAIN_STRENGTH = 0.36
// For colours given as anything but #rrggbb, e.g. a CSS variable.
const FALLBACK_GRAIN_AMPLITUDE = 0.45

/**
 * A brand colour under soft film grain, as on customer logo cards. `screen`
 * can only lift a channel by its distance to white, and `multiply` only darken
 * it by its distance to black, so light colours get dark specks and the
 * amplitude is scaled to that headroom to keep the grain equally visible.
 */
export function brandColorBackground(color: string) {
  const channels = parseHexColor(color)
  if (!channels) {
    return {
      backgroundColor: color,
      backgroundImage: buildGrainBackground({
        amplitude: FALLBACK_GRAIN_AMPLITUDE,
        tileSize: BRAND_GRAIN_TILE_SIZE,
      }),
      backgroundBlendMode: 'screen',
    } as const
  }

  const brightness = channels.reduce((sum, channel) => sum + channel, 0) / 765
  const hasDarkSpecks = isLightColor(color)
  const headroom = hasDarkSpecks ? brightness : 1 - brightness
  return {
    backgroundColor: color,
    backgroundImage: buildGrainBackground({
      amplitude: Math.min(1, BRAND_GRAIN_STRENGTH / headroom),
      tileSize: BRAND_GRAIN_TILE_SIZE,
      invert: hasDarkSpecks,
    }),
    backgroundBlendMode: hasDarkSpecks ? 'multiply' : 'screen',
  } as const
}
